import axios, { AxiosInstance } from 'axios';
import { Company, SuperUser, License, Course } from '../types';

// Get the API URL from environment or try common development ports
export const getAPIBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL + '/api/v1';
  }
  
  // In development, try common ports
  const defaultPort = 3001;
  const isDevelopment = window.location.hostname === 'localhost';
  return isDevelopment 
    ? `http://localhost:${defaultPort}/api/v1`
    : 'https://app.csuitesscorecard.online/api/v1';
};

const API_BASE_URL = getAPIBaseURL();




interface ApiInstance extends AxiosInstance {
  // ... existing interface properties ...
  
  // Training Courses
  getCourses(tenantId: string): Promise<any>;
  createCourse(data: { name: string; description: string; status: 'active' | 'inactive' }): Promise<any>;
  updateCourse(courseId: string, data: { name: string; description: string; status: 'active' | 'inactive' }): Promise<any>;
  deleteCourse(courseId: string): Promise<any>;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
}) as ApiInstance;

// Public API instance without auth requirements
export const publicApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false
});

// Implement training course methods
api.getCourses = (tenantId: string) => api.get<ApiResponse<Course[]>>(`/training-courses/${tenantId}`);
api.createCourse = (data: { name: string; description: string; status: 'active' | 'inactive' }) => 
  api.post<ApiResponse<Course>>('/training-courses', data);
api.updateCourse = (courseId: string, data: { name: string; description: string; status: 'active' | 'inactive' }) => 
  api.put<ApiResponse<Course>>(`/training-courses/${courseId}`, data);
api.deleteCourse = (courseId: string) => api.delete(`/training-courses/${courseId}`);

interface ApiResponse<T> {
  data: T;
}

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    if(config.url === '/auth/callback') {
      return config;
    } else {
      const token = sessionStorage.getItem('auth_token');
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn(`No auth token available for request to: ${config.url}`);
      }
      
      return config;
    }
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add retry logic for connection errors
api.interceptors.response.use(
  (response) => {
    if (response.data?.data) {
      return response;
    }
    return { ...response, data: { data: response.data } };
  },
  (error) => {
    if (error.response) {
      // Don't log 404 errors for calibration APIs (not yet implemented)
      // Get URL from various possible locations
      const relativeUrl = error.config?.url || '';
      const baseURL = error.config?.baseURL || '';
      const fullUrl = baseURL ? `${baseURL}${relativeUrl}` : relativeUrl;
      const urlToCheck = fullUrl || relativeUrl || '';
      
      // Check if this is a calibration API 404 error
      // Match calibration APIs in the URL string
      const urlString = String(urlToCheck || relativeUrl || error.config?.url || '');
      const isCalibration404 = error.response.status === 404 && 
        (urlString.indexOf('calibration-committee') !== -1 || 
         urlString.indexOf('calibration-sessions') !== -1);
      
      // Only log non-calibration 404s or non-404 errors
      if (!isCalibration404) {
        console.error(`API Error ${error.response.status} for ${urlToCheck}:`, error.response.data);
      }
      // Silently handle 404s for calibration APIs - backend not yet implemented
      
      // Check if we're on a public route - don't redirect to login
      const publicRoutes = ['/feedback', '/feedback/submit', '/organization-ceo-feedback/submit'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));
      
      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        console.error('Unauthorized request - check token validity');
        // Don't redirect to login if we're on a public route
        if (!isPublicRoute) {
          // Only clear token and redirect if it's an auth issue, not a missing token
          if (error.response.data?.message !== 'No token provided') {
            sessionStorage.removeItem('auth_token');
            window.location.href = '/login';
          }
        }
      }
      if (error.response.status === 403) {
        // Don't redirect on public routes
        if (!isPublicRoute) {
          if (error.response.data?.error === 'consent_required' && error.response.data?.consentUrl) {
            window.location.href = `/consent?consentUrl=${encodeURIComponent(error.response.data.consentUrl)}`;
            return Promise.reject(error);
          }
          // Backend sends licenseError: true (e.g. from licenseCheck middleware)
          if (error.response.data?.licenseError || error.response.data?.error === 'license_error') {
            window.location.href = '/license-error';
            return Promise.reject(error);
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Export the api instance for use in other files
export { api };

// Company API
export const companyAPI = {
  getAll: () => api.get<ApiResponse<Company[]>>('/companies'),
  create: (data: Omit<Company, '_id' | '__v'>) => api.post<ApiResponse<Company>>('/companies', data),
  update: (id: string, data: Partial<Omit<Company, '_id' | '__v'>>) => 
    api.put<ApiResponse<Company>>(`/companies/${id}`, data),
  delete: (id: string) => api.delete(`/companies/${id}`),
};

// SuperUser API
export const superUserAPI = {
  getAll: () => api.get<ApiResponse<SuperUser[]>>('/super-users'),
  create: (data: Omit<SuperUser, '_id' | '__v'>) => api.post<ApiResponse<SuperUser>>('/super-users', data),
  update: (id: string, data: Partial<Omit<SuperUser, '_id' | '__v'>>) => 
    api.put<ApiResponse<SuperUser>>(`/super-users/${id}`, data),
  delete: (id: string) => api.delete(`/super-users/${id}`),
};

// License API
export const licenseAPI = {
  getAll: () => api.get<ApiResponse<License[]>>('/licenses'),
  create: (data: Omit<License, '_id' | '__v'>) => api.post<ApiResponse<License>>('/licenses', data),
  update: (id: string, data: Partial<Omit<License, '_id' | '__v'>>) => 
    api.put<ApiResponse<License>>(`/licenses/${id}`, data),
  delete: (id: string) => api.delete(`/licenses/${id}`),
};

// Training Courses
export const courseAPI = {
  getAll: (tenantId: string) => api.get<ApiResponse<Course[]>>(`/training-courses/${tenantId}`),
  create: (data: Omit<Course, '_id' | 'createdAt' | 'updatedAt' | 'tenantId'>) => 
    api.post<ApiResponse<Course>>('/training-courses', data),
  update: (courseId: string, data: Partial<Omit<Course, '_id' | 'createdAt' | 'updatedAt' | 'tenantId'>>) => 
    api.put<ApiResponse<Course>>(`/training-courses/${courseId}`, data),
  delete: (courseId: string) => api.delete(`/training-courses/${courseId}`),
}; 

